import { motion } from "framer-motion";
import { Palette, Puzzle, Star, Gamepad2, TreeDeciduous, Scissors, Footprints, Search, Brain, Users } from "lucide-react";

const ActivitiesPage = () => {
  const artsAndCrafts = [
    {
      title: "Paper Plate Cow Mask",
      description: "Create your own Callie mask! Paint a paper plate white with black spots, add ears cut from cardboard, and don't forget the pink nose. Use an elastic string to wear it!",
      materials: "Paper plate, paint, cardboard, elastic string, glue"
    },
    {
      title: "Farm Animal Finger Puppets",
      description: "Cut out simple animal shapes from felt or paper - a cow, pig, and chicken. Glue them to cardboard tubes that fit over your fingers. Put on a Marchfield Meadows show!",
      materials: "Felt or paper, cardboard tubes, glue, markers"
    },
    {
      title: "Magnifying Glass Detective Kit",
      description: "Make Callie's detective kit! Decorate a small box to hold 'evidence.' Create a magnifying glass from cardboard and plastic wrap. Add a small notebook for clues!",
      materials: "Small box, cardboard, plastic wrap, notebook, stickers"
    },
    {
      title: "Farm Scene Collage",
      description: "Cut out pictures of farm animals from magazines or print them out. Glue them onto green and blue paper to create your own Marchfield Meadows scene. Add cotton balls for clouds!",
      materials: "Magazines, paper, glue, cotton balls, crayons"
    }
  ];

  const outdoorAdventures = [
    {
      title: "Backyard Detective Hunt",
      description: "Hide small 'clues' around your yard - colorful stones, paper footprints, or toy animals. Give your child a list of things to find. Can they solve the mystery like Callie?",
      tips: "Use picture clues for younger children"
    },
    {
      title: "Animal Sound Safari",
      description: "Go for a walk and listen carefully. How many different animal sounds can you hear? Birds, dogs, cats? Talk about what sounds farm animals make and try to imitate them!",
      tips: "Make it a game - who can make the best cow 'moo'?"
    },
    {
      title: "Nature Observation Walk",
      description: "Like Callie, practice being observant! Go on a walk and notice small details - different leaf shapes, animal tracks, or interesting bugs. Bring a notebook to draw what you find.",
      tips: "Ask 'What do you notice?' and 'What does this remind you of?'"
    },
    {
      title: "Farm Animal Movement Game",
      description: "In the backyard, call out different farm animals and move like them! Waddle like a duck, hop like a rabbit, strut like a rooster. Great for burning energy!",
      tips: "Take turns being the 'caller' - kids love being in charge!"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Mystery Mate Training
          </h1>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#E63946] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Become a Marchfield Meadows Mystery Mate!
          </h2>
          <div className="max-w-3xl mx-auto tactile-card p-6 bg-[#E9C46A]/10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Search className="w-8 h-8 text-[#E63946]" />
              <Brain className="w-8 h-8 text-[#2A9D8F]" />
              <Users className="w-8 h-8 text-[#457B9D]" />
            </div>
            <p className="text-lg text-[#1D3557] font-medium leading-relaxed">
              A <strong>Mystery Mate</strong> is someone who joins Callie in solving clues and building their detective skills! 
              Mystery Mates sharpen their <strong>observation</strong> abilities to notice details others might miss, 
              develop <strong>critical thinking</strong> to piece together clues, and practice <strong>working together</strong> because 
              the best mysteries are solved as a team. Through these fun activities, you'll train like a true Marchfield Meadows detective!
            </p>
          </div>
        </motion.div>

        {/* Arts & Crafts Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-[#E9C46A] rounded-2xl flex items-center justify-center border-4 border-[#1D3557]">
              <Scissors className="w-8 h-8 text-[#1D3557]" />
            </div>
            <h2 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Arts & Crafts Time
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {artsAndCrafts.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tactile-card p-6"
                data-testid={`craft-activity-${index}`}
              >
                <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {activity.title}
                </h3>
                <p className="text-[#1D3557] font-medium mb-4">
                  {activity.description}
                </p>
                <div className="bg-[#E9C46A]/20 rounded-xl p-3 border-2 border-[#1D3557]/20">
                  <p className="text-sm text-[#1D3557] font-bold">
                    <Palette className="w-4 h-4 inline mr-2" />
                    Materials: {activity.materials}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Outdoor Adventures Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-[#2A9D8F] rounded-2xl flex items-center justify-center border-4 border-[#1D3557]">
              <TreeDeciduous className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Outdoor Adventures
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {outdoorAdventures.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tactile-card p-6"
                data-testid={`outdoor-activity-${index}`}
              >
                <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {activity.title}
                </h3>
                <p className="text-[#1D3557] font-medium mb-4">
                  {activity.description}
                </p>
                <div className="bg-[#2A9D8F]/20 rounded-xl p-3 border-2 border-[#1D3557]/20">
                  <p className="text-sm text-[#1D3557] font-bold">
                    <Footprints className="w-4 h-4 inline mr-2" />
                    Tip: {activity.tips}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* More Activities Coming Soon */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#1D3557] mb-8" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            More Mystery Mate Training Coming Soon!
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Palette className="w-10 h-10" />, title: "Coloring Pages", color: "bg-[#E9C46A]" },
              { icon: <Puzzle className="w-10 h-10" />, title: "Farm Puzzles", color: "bg-[#2A9D8F]" },
              { icon: <Star className="w-10 h-10" />, title: "Fun Fact Cards", color: "bg-[#457B9D]" },
              { icon: <Gamepad2 className="w-10 h-10" />, title: "Detective Games", color: "bg-[#E63946]" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tactile-card p-6 text-center"
              >
                <div className={`${item.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white border-4 border-[#1D3557]`}>
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {item.title}
                </h3>
                <p className="text-sm text-[#1D3557]/70 font-medium mt-2">Coming Soon!</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Activity Book Promo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="tactile-card p-8 md:p-12 bg-[#E63946]/10"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <img
              src="https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/7cmear17_image.png"
              alt="Activity Book Cover"
              className="w-48 h-auto rounded-2xl border-4 border-[#1D3557] shadow-[6px_6px_0px_0px_rgba(29,53,87,1)]"
            />
            <div>
              <h3 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Want More Mystery Mate Training?
              </h3>
              <p className="text-[#1D3557] font-medium mb-4">
                The Detective Diaries of Marchfield Meadows Activity Book is coming soon! Packed with coloring pages, puzzles, mazes, and more farm fun to sharpen your detective skills!
              </p>
              <p className="text-[#1D3557]/70 font-medium italic">
                Coming soon to Amazon via KDP — join the Early Readers List on the home page to be the first to know.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ActivitiesPage;
